import { defineRule, type Rule } from '../types.js';

/**
 * Правила по признакам автоматизации.
 *
 * Важная оговорка, зашитая в веса: сам по себе факт автоматизации не является
 * мошенничеством. Тестировщик, запустивший Playwright на своём же сайте, ничем
 * не провинился. Поэтому в этой группе тяжёлые веса даются только прямым
 * уликам — служебным объектам драйверов и заявленному headless-режиму, — а
 * косвенные признаки идут с пониженной уверенностью.
 */

function hasGoogleChromeBrand(
  brands: { brand: string; version: string }[] | null | undefined,
): boolean {
  return (brands ?? []).some((brand) => brand.brand.toLowerCase() === 'google chrome');
}

export const automationRules: Rule[] = [
  defineRule({
    meta: {
      id: 'auto.webdriver-flag',
      title: 'Браузером управляет программа',
      severity: 'critical',
      category: 'automation',
      flags: ['AUTOMATION', 'BOT'],
      weight: 40,
      evidenceTier: 'direct',
      evidenceGroup: 'webdriver',
      requires: ['automation'],
    },
    evaluate(signals) {
      if (signals.automation?.webdriver !== true) return null;
      return {
        explanation:
          'Браузер сам сообщает, что находится под управлением программы: свойство ' +
          'navigator.webdriver включено. Его выставляют Selenium, Puppeteer и Playwright.',
        evidence: [{ label: 'navigator.webdriver', expected: 'false', actual: 'true' }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.driver-artifacts',
      title: 'В окружении найдены следы драйвера автоматизации',
      severity: 'critical',
      category: 'automation',
      flags: ['AUTOMATION', 'BOT'],
      weight: 40,
      evidenceTier: 'direct',
      evidenceGroup: 'driver-artifacts',
      requires: ['automation'],
    },
    evaluate(signals) {
      const automation = signals.automation;
      const artifacts = [
        ...(automation?.driverArtifacts ?? []),
        ...(automation?.automationGlobals ?? []),
        ...(automation?.documentAttributes ?? []),
      ];
      if (artifacts.length === 0) return null;

      return {
        explanation:
          'В глобальной области или в разметке страницы найдены служебные объекты, ' +
          'которые оставляют драйверы автоматизации. Обычный браузер их не создаёт.',
        evidence: [{ label: 'Найденные объекты', detail: artifacts.slice(0, 10).join(', ') }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.headless-ua',
      title: 'User-Agent прямо указывает на headless-режим',
      severity: 'critical',
      category: 'automation',
      flags: ['AUTOMATION', 'BOT'],
      weight: 45,
      evidenceTier: 'direct',
      evidenceGroup: 'automation-ua',
      requires: ['automation', 'navigator'],
    },
    evaluate(signals) {
      if (!signals.automation?.uaDeclaresHeadless) return null;
      return {
        explanation:
          'User-Agent содержит прямое указание на автоматизированную или безоконную сборку ' +
          '(HeadlessChrome, PhantomJS, Electron и подобные).',
        evidence: [{ label: 'User-Agent', actual: signals.navigator?.userAgent ?? '—' }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.headless-hints',
      title: 'Окружение похоже на браузер без графического интерфейса',
      severity: 'high',
      category: 'automation',
      flags: ['AUTOMATION'],
      weight: 25,
      evidenceTier: 'corroborating',
      evidenceGroup: 'headless-shape',
      requires: ['automation'],
    },
    evaluate(signals) {
      const hints = signals.automation?.headlessHints ?? [];
      if (hints.length < 2) return null;

      return {
        // Каждый признак по отдельности объясним, вместе — уже закономерность.
        confidence: Math.min(0.4 + hints.length * 0.15, 1),
        explanation:
          'Несколько независимых признаков указывают на браузер, запущенный без окна: ' +
          'у настоящего окна есть панель вкладок, плагины и согласованные разрешения.',
        evidence: hints.map((hint) => ({ label: 'Признак', detail: hint })),
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.window-geometry',
      title: 'Геометрия окна не похожа на настоящее окно браузера',
      severity: 'medium',
      category: 'automation',
      flags: ['AUTOMATION'],
      weight: 15,
      evidenceTier: 'corroborating',
      evidenceGroup: 'window-shape',
      requires: ['automation', 'screen'],
    },
    evaluate(signals) {
      if (!signals.automation?.windowGeometrySuspicious) return null;
      const screen = signals.screen;

      return {
        confidence: 0.7,
        explanation:
          'Размеры окна невозможны для обычного браузера: внешнее окно всегда выше ' +
          'области страницы на высоту вкладок и адресной строки.',
        evidence: [
          { label: 'Окно', actual: `${screen?.outerWidth ?? '?'} × ${screen?.outerHeight ?? '?'}` },
          { label: 'Область страницы', actual: `${screen?.innerWidth ?? '?'} × ${screen?.innerHeight ?? '?'}` },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.chrome-runtime-missing',
      title: 'Заявленный Google Chrome потерял часть window.chrome',
      severity: 'medium',
      category: 'spoofing',
      flags: ['ANTIDETECT'],
      weight: 22,
      evidenceTier: 'corroborating',
      evidenceGroup: 'engine-surface',
      requires: ['automation', 'navigator'],
    },
    evaluate(signals) {
      const parsed = signals.navigator?.parsed;
      const chrome = signals.automation?.chromeObject;
      const uaData = signals.navigator?.uaData;
      const claimsGoogleChrome =
        hasGoogleChromeBrand(uaData?.brands) || hasGoogleChromeBrand(uaData?.fullVersionList);
      if (
        parsed?.engine !== 'Blink' ||
        parsed.isMobile ||
        !claimsGoogleChrome ||
        !chrome?.present ||
        !chrome.hasCsi ||
        !chrome.hasLoadTimes ||
        chrome.hasRuntime
      ) {
        return null;
      }

      return {
        confidence: 0.68,
        explanation:
          'Client Hints заявляют Google Chrome, а объект window.chrome выглядит как усечённая ' +
          'сборка Chromium: есть chrome.csi и chrome.loadTimes, но нет chrome.runtime. ' +
          'У производных Chromium-браузеров это бывает, поэтому признак слабый; для профиля, ' +
          'который прямо называет себя Google Chrome, он полезен как дополнительная улика.',
        evidence: [
          { label: 'Ключи window.chrome', detail: chrome.keys.slice(0, 10).join(', ') },
          { label: 'chrome.runtime', expected: 'есть', actual: 'отсутствует' },
          { label: 'nativeFunctions', actual: String(chrome.nativeFunctions) },
        ],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.cdp',
      title: 'Активен отладочный протокол браузера',
      severity: 'low',
      category: 'automation',
      flags: ['AUTOMATION'],
      weight: 8,
      evidenceTier: 'corroborating',
      evidenceGroup: 'devtools-protocol',
      requires: ['automation'],
    },
    evaluate(signals) {
      if (!signals.automation?.cdpDetected) return null;
      return {
        // Открытые DevTools дают тот же результат, поэтому уверенность низкая
        // и вес маленький — иначе разработчик получал бы штраф за свою работу.
        confidence: 0.4,
        explanation:
          'Кто-то читает объекты страницы через отладочный протокол. Так работают ' +
          'Puppeteer и Playwright — но точно так же выглядят открытые инструменты ' +
          'разработчика, поэтому сам по себе признак ничего не доказывает.',
        evidence: [{ label: 'Способ обнаружения', detail: signals.automation.cdpMethod ?? '—' }],
      };
    },
  }),

  defineRule({
    meta: {
      id: 'auto.no-voices-desktop',
      title: 'В системе нет ни одного голоса синтеза речи',
      severity: 'medium',
      category: 'automation',
      flags: ['AUTOMATION', 'VIRTUAL_MACHINE'],
      weight: 15,
      evidenceTier: 'corroborating',
      evidenceGroup: 'device-shape',
      requires: ['os', 'navigator'],
    },
    evaluate(signals) {
      const voices = signals.os?.voices;
      const parsed = signals.navigator?.parsed;
      if (!parsed || voices === null || voices === undefined) return null;
      if (voices.length > 0 || parsed.isMobile) return null;
      // На Linux голоса часто не установлены — это нормальная конфигурация.
      if (parsed.platformFamily === 'linux') return null;

      return {
        confidence: 0.6,
        explanation:
          `В ${parsed.os} голоса синтеза речи входят в состав системы и присутствуют всегда. ` +
          'Их полное отсутствие характерно для серверных сборок и браузеров без окна.',
        evidence: [{ label: 'Найдено голосов', expected: '> 0', actual: '0' }],
      };
    },
  }),
];
